import type { QueryClient } from '@tanstack/react-query';
import { createRootRouteWithContext, createRoute, createRouter, Outlet, redirect } from '@tanstack/react-router';
import { RealtimeBridge } from './components/Realtime.tsx';
import { rt } from './lib/runtime.ts';
import { Admin } from './routes/Admin.tsx';
import { Chat } from './routes/Chat.tsx';
import { AuthCallback } from './routes/AuthCallback.tsx';
import { Bank } from './routes/Bank.tsx';
import { Character } from './routes/Character.tsx';
import { Chronicle } from './routes/Chronicle.tsx';
import { Create } from './routes/Create.tsx';
import { Deeds } from './routes/Deeds.tsx';
import { Fight } from './routes/Fight.tsx';
import { Landing } from './routes/Landing.tsx';
import { Leaderboards } from './routes/Leaderboards.tsx';
import { Privacy, Rules, Terms } from './routes/Legal.tsx';
import { Market } from './routes/Market.tsx';
import { Town } from './routes/Town.tsx';
import { Trades } from './routes/Trades.tsx';
import { Venue } from './routes/Venue.tsx';
import { ConsentBanner } from './components/Consent.tsx';

const root = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  component: () => (
    <>
      <Outlet />
      <RealtimeBridge />
      <ConsentBanner />
    </>
  ),
});
const requireAuth = () => {
  if (!rt().auth.isSignedIn()) throw redirect({ to: '/' });
};

const routeTree = root.addChildren([
  createRoute({ getParentRoute: () => root, path: '/', component: Landing }),
  createRoute({ getParentRoute: () => root, path: '/auth/callback', component: AuthCallback }),
  createRoute({ getParentRoute: () => root, path: '/rules', component: Rules }),
  createRoute({ getParentRoute: () => root, path: '/terms', component: Terms }),
  createRoute({ getParentRoute: () => root, path: '/privacy', component: Privacy }),
  createRoute({ getParentRoute: () => root, path: '/create', component: Create, beforeLoad: requireAuth }),
  createRoute({ getParentRoute: () => root, path: '/town', component: Town, beforeLoad: requireAuth }),
  createRoute({ getParentRoute: () => root, path: '/character', component: Character, beforeLoad: requireAuth }),
  createRoute({ getParentRoute: () => root, path: '/deeds', component: Deeds, beforeLoad: requireAuth }),
  createRoute({ getParentRoute: () => root, path: '/venue/$venueKey', component: Venue, beforeLoad: requireAuth }),
  createRoute({ getParentRoute: () => root, path: '/fight', component: Fight, beforeLoad: requireAuth }),
  createRoute({ getParentRoute: () => root, path: '/bank', component: Bank, beforeLoad: requireAuth }),
  createRoute({ getParentRoute: () => root, path: '/market', component: Market, beforeLoad: requireAuth }),
  createRoute({ getParentRoute: () => root, path: '/trades', component: Trades, beforeLoad: requireAuth }),
  createRoute({ getParentRoute: () => root, path: '/chronicle', component: Chronicle, beforeLoad: requireAuth }),
  createRoute({ getParentRoute: () => root, path: '/chat', component: Chat, beforeLoad: requireAuth }),
  createRoute({ getParentRoute: () => root, path: '/leaderboards', component: Leaderboards, beforeLoad: requireAuth }),
  createRoute({ getParentRoute: () => root, path: '/admin', component: Admin, beforeLoad: requireAuth }),
]);

export function makeRouter(queryClient: QueryClient) {
  return createRouter({ routeTree, context: { queryClient }, defaultPreload: 'intent' });
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof makeRouter>;
  }
}
